import { useEffect, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { IS_STANDALONE } from '@/config/standalone';

function CloseButton({ onClose }: { onClose: () => void }) {
  return (
    <button
      type="button"
      onClick={onClose}
      aria-label="Close"
      className="absolute right-3 top-3 z-10 flex h-9 w-9 items-center justify-center rounded-full border border-grey-200 bg-white text-grey-500 shadow-md transition-colors hover:bg-grey-50 hover:text-secondary sm:-right-3 sm:-top-3"
    >
      <X className="h-5 w-5" />
    </button>
  );
}

/**
 * Full-screen takeover shell: a blurred backdrop, a corner close button,
 * Escape-to-close, and body-scroll lock.
 *
 * - Default (no `maxWidthClass`): the child fills the inset panel and scrolls
 *   internally — used by the full-bleed task board.
 * - With `maxWidthClass` (e.g. "max-w-4xl"): the child renders at its natural
 *   height as a centered card — vertically centered when short, scrolling from
 *   the top when tall. The close button rides on the card's corner.
 */
export function Takeover({
  onClose,
  children,
  maxWidthClass,
}: {
  onClose: () => void;
  children: ReactNode;
  maxWidthClass?: string;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose]);

  return createPortal(
    // Offset past the platform app-bar rail (injected at ~z-9999) when embedded,
    // so its content isn't hidden under the rail. Standalone dev has no rail.
    //
    // BOTH AXES. The rail is a left column on desktop and a 56px TOP BAR on a
    // phone, where it pushes the page down with a body margin — which a FIXED
    // overlay ignores, so the takeover slid underneath it and ate the title
    // (screenshot, 2026-08-27). Below lg we start under the bar instead.
    <div
      className={cn(
        'fixed inset-0 z-[60]',
        !IS_STANDALONE && 'top-[var(--solid-rail-height,56px)] lg:top-0 lg:left-[var(--solid-rail-width,52px)]',
      )}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-secondary/50 backdrop-blur-sm" onClick={onClose} />

      {maxWidthClass ? (
        // Centered-card mode: card centers vertically; it scrolls internally
        // (the child's surface owns the scrollbar), so nothing floats at the edge.
        <div className="absolute inset-0 flex items-center justify-center p-4 sm:inset-4 lg:inset-8">
          <div className={cn('relative w-full', maxWidthClass)}>
            <CloseButton onClose={onClose} />
            {children}
          </div>
        </div>
      ) : (
        // Full-bleed mode: child fills the panel and scrolls internally.
        <div className="absolute inset-0 sm:inset-4 lg:inset-8">
          <div className="relative h-full w-full">
            <CloseButton onClose={onClose} />
            {children}
          </div>
        </div>
      )}
    </div>,
    document.body,
  );
}
